import { maybeSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import loadableBase, {
  DefaultComponent,
  LoadableComponent,
  OptionsWithResolver,
  OptionsWithoutResolver,
  lazy,
} from "@loadable/component";
import { shouldConcurrentRender } from "@faire/retailer-shared/lib/ssr";
import React from "react";

import { handleComponentLoadError } from "@faire/retailer/lib/loadChunkErrors";

type NullFallback = ReturnType<typeof handleComponentLoadError>;

const NullComponent = () => null;

type LoadFnWithErrorHandling<Props, Module = DefaultComponent<Props>> = (
  props: Props
) => Promise<Module | NullFallback>;

/**
 * A thin wrapper around @loadable/component's loadable and lazy functions,
 * that suspends when rendering on the client and when rendering in React 18 mode.
 */
const newLoadable = <Props, Module = DefaultComponent<Props>>(
  loadFn: LoadFnWithErrorHandling<Props, Module>,
  options?: OptionsWithoutResolver<Props> | OptionsWithResolver<Props, Module>
): LoadableComponent<Props> => {
  if (options && "resolveComponent" in options) {
    const optionsWithFallback: OptionsWithResolver<Props, Module> = {
      ...options,
      resolveComponent: (m, p) => {
        return options.resolveComponent(m, p) || NullComponent;
      },
    };

    return maybeSSR() && !shouldConcurrentRender()
      ? loadableBase(
          loadFn as (props: Props) => Promise<Module>,
          optionsWithFallback
        )
      : // Note: This is what lazy does, but its types are incorrect.
        // See https://github.com/gregberge/loadable-components/blob/4e99a97c5ae958c4f18ac82b7e1a3566bc81c912/packages/component/src/createLoadable.js#L153
        loadableBase<Props, Module>(
          loadFn as (props: Props) => Promise<Module>,
          {
            ...optionsWithFallback,
            suspense: true,
          } as { suspense: boolean } & OptionsWithResolver<Props, Module>
        );
  }

  return maybeSSR() && !shouldConcurrentRender()
    ? loadableBase(loadFn as (props: Props) => Promise<DefaultComponent<Props>>)
    : lazy(loadFn as (props: Props) => Promise<DefaultComponent<Props>>);
};

/**
 * Singleton store which maps symbols to loader instances in each Zone.
 */
class LoadableStore {
  static get = singletonGetter(LoadableStore);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FIXME(explicitAny) Please remove any and use an appropriate type here. See more: https://faire.link/ban-typescript-any
  private componentMap = new Map<symbol, LoadableComponent<any>>();

  getOrInsert = (
    sym: symbol,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FIXME(explicitAny) Please remove any and use an appropriate type here. See more: https://faire.link/ban-typescript-any
    loadFn: LoadFnWithErrorHandling<any, unknown>,
    options?: // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FIXME(explicitAny) Please remove any and use an appropriate type here. See more: https://faire.link/ban-typescript-any
    OptionsWithoutResolver<any> | OptionsWithResolver<unknown, unknown>
  ) => {
    if (!this.componentMap.has(sym)) {
      this.componentMap.set(sym, newLoadable(loadFn, options));
    }

    return this.componentMap.get(sym)!;
  };
}

/**
 * Zone-aware wrapper around @loadable/component.
 *
 * The loader components generated by this function have the following behaviour:
 *  - in React 16 server rendering, the loader is synchronous (does not suspend)
 *  - in React 18 server rendering, the loader suspends the first time it is
 *  rendered in each Zone. Since partial hydration boundaries depend on React
 *  being suspended, this ensures that partial hydration boundaries are
 *  correctly placed.
 *  - in client rendering, the loader suspends the first time it is rendered.
 *
 * Note that the @loadable/babel-plugin requires a precise text-match on `loadable`,
 * which is why we don't default export here and don't call it `lazy`.
 */
export const loadable = <Props, Module = DefaultComponent<Props>>(
  loadFn: LoadFnWithErrorHandling<Props, Module>,
  options?: OptionsWithoutResolver<Props> | OptionsWithResolver<Props, Module>
): React.ComponentType<Props> => {
  // Identifier for this loadable component.
  const sym = Symbol();

  const component = React.forwardRef((props: Props, ref) => {
    const Component = LoadableStore.get().getOrInsert(sym, loadFn, options);
    return <Component {...props} ref={ref} />;
  }) as unknown as React.ComponentType<Props> & { preload: () => void };

  // Do NOT call component.preload() directly. Instead make a wrapper function in
  // a new file in src/lib/lazyPreload_DO_NOT_RENAME (must be a DEFAULT export) that
  // uses the preload() helper function and call that instead.
  component.preload = () => {
    LoadableStore.get().getOrInsert(sym, loadFn, options).preload();
  };

  return component;
};
